import { config } from '@brands/config/config';
import PasswordlessVerify from '@brands/Auth/PasswordlessVerify/PasswordlessVerify';
import {
  passwordlessRequest,
  PasswordlessRequestResponse,
} from '@brands/services/auth/passwordlessRequest';
import { usePostAuth } from '@brands/hooks/usePostAuth';
import { displayErrorDetails } from '@brands/Utils/displayError';
import { isValidEmail, isValidPhoneNumber } from '@brands/Utils/validation';
import React, { FC, FormEvent, useState } from 'react';

import styles from './styles.module.scss';

const normalize = (raw: string): string => {
  const trimmed = raw.trim();
  return isValidPhoneNumber(trimmed) ? `+1${trimmed.replace(/\s+|[()-.]/g, '')}` : trimmed.toLowerCase();
};

const PasswordlessSignIn: FC = () => {
  const { triggerPostAuth } = usePostAuth();
  const [usernameInput, setUsernameInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<{
    username: string;
    response: PasswordlessRequestResponse;
  } | null>(null);

  const inputValid = isValidEmail(usernameInput) || isValidPhoneNumber(usernameInput);

  const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!inputValid) return;

    setLoading(true);
    try {
      const username = normalize(usernameInput);
      const response = await passwordlessRequest({
        username,
        organization_id: config.organization_id,
        ...(config.division_id && { division_id: config.division_id }),
        ...(config.practice_id && { practice_id: config.practice_id }),
      });
      setStep({ username, response });
    } catch (error) {
      displayErrorDetails(error);
    } finally {
      setLoading(false);
    }
  };

  if (step) {
    return (
      <PasswordlessVerify
        username={step.username}
        initialResponse={step.response}
        onCancel={() => setStep(null)}
        onVerified={async (verifyResponse) => {
          await triggerPostAuth(verifyResponse, { event: 'login' });
        }}
      />
    );
  }

  return (
    <div className={styles.page}>
      <form className={styles.card} onSubmit={onSubmit}>
        <h1 className={styles.title}>Sign in with a code</h1>
        <p className={styles.subtitle}>
          We’ll send you a 6-digit code to sign in. No password needed.
        </p>
        <label className={styles.label} htmlFor="passwordless-username">
          Email or phone
        </label>
        <input
          id="passwordless-username"
          type="text"
          autoComplete="username"
          autoFocus
          className={styles.input}
          value={usernameInput}
          onChange={(e) => setUsernameInput(e.target.value)}
        />
        <button type="submit" className={styles.btn} disabled={loading || !inputValid}>
          {loading ? 'Sending…' : 'Send code'}
        </button>
      </form>
    </div>
  );
};

export default PasswordlessSignIn;
